import { useState } from "react";

// Compose panel for a personal outreach email. The "Draft with AI" button asks
// the server for a grounded draft and drops it into the editable fields; the
// admin always reviews and edits before sending. Send is a normal form POST so
// the server keeps owning the redirect + flash (and it still works if the draft
// step is skipped or the AI is unavailable).
export default function OutreachComposer({
  draft_endpoint,
  send_endpoint,
  candidate_profile_id,
  csrf_token,
  first_name = "there",
  intents = [],
  default_intent,
  // When set (e.g. "diagnostic_funnel"), passed through as a hidden field so the
  // server can route the post-send redirect back to where it was sent from.
  source,
}) {
  const [intent, setIntent] = useState(default_intent || intents[0]?.key || "encourage");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  // Tracks whether the message being sent is built on an AI draft. Set when a
  // draft is dropped in; cleared if the admin wipes the body to write their own,
  // so a fully hand-written message isn't logged as AI-assisted.
  const [aiAssisted, setAiAssisted] = useState(false);
  const [drafting, setDrafting] = useState(false);
  const [note, setNote] = useState(null);

  function editBody(value) {
    setBody(value);
    if (aiAssisted && value.trim() === "") setAiAssisted(false);
  }

  async function draftWithAI() {
    setDrafting(true);
    setNote(null);
    try {
      const res = await fetch(draft_endpoint, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          "X-CSRF-Token": csrf_token,
        },
        body: JSON.stringify({ intent }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setNote(data.error || "Couldn't draft a message — please write one below.");
        return;
      }
      setSubject(data.subject || "");
      setBody(data.body || "");
      setAiAssisted(true);
    } catch {
      setNote("Couldn't reach the drafting service — please write one below.");
    } finally {
      setDrafting(false);
    }
  }

  return (
    <form method="post" action={send_endpoint} className="admin-form">
      <input type="hidden" name="authenticity_token" value={csrf_token} />
      <input type="hidden" name="candidate_profile_id" value={candidate_profile_id} />
      <input type="hidden" name="ai_assisted" value={aiAssisted ? "true" : "false"} />
      {source && <input type="hidden" name="source" value={source} />}

      {/* Intent + AI draft: select on the left, draft button right-aligned, hint below */}
      <div className="admin-form-group">
        <div style={{ display: "flex", gap: 16, alignItems: "flex-end", flexWrap: "wrap" }}>
          <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-2)", minWidth: 220 }}>
            <label className="admin-form-group__label" htmlFor="outreach_intent">Intent</label>
            <select
              id="outreach_intent"
              name="intent"
              className="admin-select"
              value={intent}
              onChange={(e) => setIntent(e.target.value)}
            >
              {intents.map((it) => (
                <option key={it.key} value={it.key}>{it.label}</option>
              ))}
            </select>
          </div>

          <button
            type="button"
            className="admin-btn --sm"
            onClick={draftWithAI}
            disabled={drafting}
            style={{ marginLeft: "auto" }}
          >
            {drafting ? (
              <><i className="fa-solid fa-circle-notch fa-spin" />Drafting…</>
            ) : (
              <><i className="fa-solid fa-wand-magic-sparkles" />Draft with AI</>
            )}
          </button>
        </div>
        <p className="admin-form-group__hint" style={{ marginTop: "var(--space-2)", color: note ? "var(--err)" : undefined }}>
          {note || `AI drafts from ${first_name}'s performance — you can edit everything before sending.`}
        </p>
      </div>

      <div className="admin-form-group">
        <label className="admin-form-group__label" htmlFor="outreach_subject">Subject</label>
        <input
          id="outreach_subject"
          name="subject"
          className="admin-input"
          placeholder="Checking in on your CRE prep"
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
        />
      </div>

      <div className="admin-form-group">
        <label className="admin-form-group__label" htmlFor="outreach_body">Message</label>
        <textarea
          id="outreach_body"
          name="body"
          className="admin-textarea"
          rows={10}
          placeholder={`Hi ${first_name},\n\n…`}
          value={body}
          onChange={(e) => editBody(e.target.value)}
        />
        {aiAssisted && (
          <p className="admin-form-group__hint" style={{ color: "var(--accent-ink)" }}>
            <i className="fa-solid fa-wand-magic-sparkles" style={{ marginRight: 6 }} />
            AI-drafted — review and make it yours before sending.
          </p>
        )}
      </div>

      <div className="admin-form-actions">
        <button type="submit" className="admin-btn --accent">Send message</button>
      </div>
    </form>
  );
}
